import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, AlertTriangle, Droplet, Shield, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const FloodResiliencePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Floods, Dirty Water & Filtration Resilience | CTC Waterworks"
        description="Learn how historic floods and extreme weather affect irrigation, municipal, and industrial water systems. Practical guidance on dirty water, debris, and filtration resilience from CTC Waterworks."
        canonicalPath="/flood-resilience"
      />
      <Navbar />

      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnptMCAzNmMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnpNNTQgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6TTYgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6IiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-10"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-red-600/20 text-red-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-red-500/30">
              <AlertTriangle size={16} />
              <span>Flood & Extreme Weather Resource Hub</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Floods, Dirty Water & Your Water Systems
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Historic floods in the Pacific Northwest and beyond are leaving behind more than headlines—they're reshaping rivers, canals, and ponds for years. This hub helps growers, utilities, and operators understand what that means for intakes, pumps, and filtration.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="tel:+15099017476"
              className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
            >
              <Phone size={20} />
              <span>Call Now (509) 901-7476</span>
            </a>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
            >
              <span>Request a Call Back ASAP</span>
              <ArrowRight size={20} />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-gray-400 text-center mt-6"
          >
            Flood recovery decisions move fast. Our engineering team prioritizes calls related to flood and dirty-water issues and typically responds within one business day.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Floods End, But Dirty Water Sticks Around
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                When rivers jump their banks and fields go under, the damage isn't just fences and roads. Floods scour streambanks, move sediment, and flush organics, manure, and debris into every low point in the watershed.
              </p>
              <p>
                As flows drop and snowpack builds, all that material doesn't disappear—it settles into rivers, canals, ponds, and sumps. Months later, when you open valves and start pumps, it shows up as:
              </p>
              <ul className="space-y-3 my-6">
                {[
                  'Higher sediment and turbidity at river and canal intakes',
                  'More trash, branches, and plastics at screens and trash racks',
                  'Nutrient-rich water that feeds algae and biofilm in ponds and sumps',
                  'Shorter filter run times and more frequent cleaning',
                  'Plugged nozzles, emitters, and strainers'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-red-600 flex-shrink-0 mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                Whether you manage orchards, a municipal pump station, or a process-water intake at a plant, floods change your water for multiple seasons, not just during the storm.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Built for the People Standing at the Pump
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Droplet,
                  title: 'Growers & Irrigation Managers',
                  items: [
                    'Drip and micro-sprinkler systems on surface water',
                    'River, canal, or pond-fed irrigation',
                    'Filtration systems that already struggle with silt and algae'
                  ]
                },
                {
                  icon: Shield,
                  title: 'Municipal & District Utilities',
                  items: [
                    'Raw water intakes on rivers and canals',
                    'Wastewater and stormwater pump stations in flood-prone areas',
                    'Irrigation, drainage, and diking districts managing canals and culverts'
                  ]
                },
                {
                  icon: Users,
                  title: 'Industrial & Food Processors',
                  items: [
                    'Process and cooling water drawn from rivers, canals, or ponds',
                    'Washdown and spray systems sensitive to debris and scale',
                    'Lagoons and settling ponds affected by flood overflow'
                  ]
                }
              ].map((category, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-gray-200 shadow-lg"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center mb-6">
                    <category.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{category.title}</h3>
                  <ul className="space-y-3">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Common Flood-Driven Water Problems We See
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Plugged Intakes & Pump Suction',
                  description: 'Flood debris and driftwood choke suction screens and culvert inlets, forcing crews to rake screens at the worst possible time.'
                },
                {
                  title: 'Short Filter Run Times',
                  description: 'Suspended solids and fine silt blow through pre-screens and hammer downstream filters, turning "once a week" cleaning into "every few hours."'
                },
                {
                  title: 'Algae & Biofilm in Ponds and Sumps',
                  description: 'Nutrient-rich floodwater sets the stage for heavy algae growth and biofilm once temperatures rise, increasing plugging and odor issues.'
                },
                {
                  title: 'Safety & Labor Strain',
                  description: 'Operators and field crews are sent into dangerous conditions—fast-moving water, soft banks, night work—to keep systems limping along.'
                }
              ].map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{problem.title}</h3>
                  <p className="text-gray-700">{problem.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Where CTC Fits Into Flood Recovery & Resilience
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              CTC Waterworks focuses on the parts of your system that physically touch dirty water: intakes, screens, and filtration. Our role in flood recovery is not emergency pumping or temporary damming—we help you repair and upgrade the permanent hardware so the next event hurts less.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Irrigation & Agriculture</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Post-flood intake protection with self-cleaning suction screens</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Inline filtration sized for higher sediment loads</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Winter inspections and system reviews before the next irrigation season</span>
                  </li>
                </ul>
                <Link
                  to="/flood-irrigation-filtration"
                  className="inline-flex items-center space-x-2 text-green-700 font-semibold hover:text-green-800 transition-colors"
                >
                  <span>Learn more about post-flood irrigation filtration</span>
                  <ArrowRight size={18} />
                </Link>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Municipal & District Utilities</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Self-cleaning screens for raw water, stormwater, and district pump intakes</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Filtration and strainers for plant services and non-potable uses</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Support for "build-back-better" pump station upgrades</span>
                  </li>
                </ul>
                <Link
                  to="/municipal/flood-resilient-intakes"
                  className="inline-flex items-center space-x-2 text-blue-700 font-semibold hover:text-blue-800 transition-colors"
                >
                  <span>Flood-resilient intakes for municipal & district systems</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-ctc-blue/10 to-ctc-blue/5 rounded-2xl p-8 border border-ctc-blue/20 mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              The Off-Season Is the Time to Fix What Floods Exposed
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              It's normal to focus on immediate clean-up first—roads, utilities, structures, and insurance. But as soon as the immediate crisis passes, there is a window where you can:
            </p>
            <ul className="space-y-3">
              {[
                'Inspect intakes, screens, and filters before they are back under full load',
                'Document where systems struggled during the flood',
                'Plan upgrades and replacements while funding and political will are available'
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle className="text-ctc-blue flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700 mt-6">
              Whether you are a grower planning for spring irrigation, or a utility scheduling capital work, this is when decisions with the biggest long-term payoffs get made.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-6">
              Need to Talk Through a Flood-Related Water Problem?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              If you're dealing with flooded intakes, dirty irrigation water, or pump stations that struggled during recent storms, a quick conversation with our team can save weeks of trial and error later.
            </p>
            <p className="text-lg text-gray-400 mb-8">
              We prioritize calls related to flood and dirty-water issues and typically return messages within one business day.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="tel:+15099017476"
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
              >
                <Phone size={20} />
                <span>Call Now (509) 901-7476</span>
              </a>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <span>Request a Call Back ASAP</span>
                <ArrowRight size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              We prioritize flood and dirty-water calls and aim to respond as quickly as possible, typically within one business day.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="flood_resilience_page"
      />
    </div>
  );
};

export default FloodResiliencePage;
